import React, { useRef, useEffect, useState } from "react";
import { Box, Button, styled } from "@mui/material";
import { PerkCard } from "./PerkCard";
import type {
  PerkQuirkSentimentValue,
  PerkQuirkSentimentRecord,
} from "../../../types";

interface PerkQuirkCarouselItem {
  id: string;
  name: string;
  description?: string;
  type: "perk" | "quirk";
}

interface PerkQuirkCarouselProps {
  items: PerkQuirkCarouselItem[];
  currentIndex: number;
  onNext: () => void;
  onPrevious: () => void;
  onGoToItem: (index: number) => void;
  completedItems?: string[];
  sentiments?: PerkQuirkSentimentRecord[];
  onSentimentChange?: (
    perksQuirksId: string,
    sentiment: PerkQuirkSentimentValue,
  ) => void;
}

const cardSpacing = 400;

const CarouselContainer = styled(Box, {
  label: "PerkQuirkCarouselContainer",
})(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "visible",
}));

const CarouselContent = styled(Box, {
  label: "PerkQuirkCarouselContent",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  position: "relative",
}));

const ScrollContainer = styled(Box, {
  label: "PerkQuirkScrollContainer",
})(() => ({
  display: "flex",
  overflow: "visible",
  scrollBehavior: "smooth",
  width: "45%",
  position: "relative",
}));

const CardsWrapper = styled(Box, {
  label: "PerkQuirkCardsWrapper",
})(() => ({
  display: "flex",
  transition: "transform 0.3s ease-in-out",
  gap: cardSpacing,
}));

const CardContainer = styled(Box, {
  label: "PerkQuirkCardContainer",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(12),
  justifyContent: "center",
}));

const BottomSection = styled(Box, {
  label: "PerkQuirkBottomSection",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
}));

const ProgressIndicators = styled(Box, {
  label: "PerkQuirkProgressIndicators",
})(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
}));

const ProgressDot = styled(Box, {
  label: "PerkQuirkProgressDot",
})<{ active?: boolean }>(({ active }) => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: active ? "#078dee" : "rgba(255, 255, 255, 0.3)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: active ? "#0570c4" : "rgba(255, 255, 255, 0.5)",
  },
}));

const ActionButtons = styled(Box, {
  label: "PerkQuirkActionButtons",
})(() => ({
  display: "flex",
  justifyContent: "flex-end",
  height: 40,
  alignItems: "center",
  width: "100%",
  maxWidth: "800px",
}));

export const PerkQuirkCarousel = ({
  items,
  currentIndex,
  onNext,
  onPrevious,
  onGoToItem,
  completedItems = [],
  sentiments = [],
  onSentimentChange,
}: PerkQuirkCarouselProps) => {
  const cardsWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollAccumulator, setScrollAccumulator] = useState(0);
  const scrollThreshold = 200;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;

  const allItemsCompleted = items.every((item) =>
    completedItems.includes(item.id),
  );

  useEffect(() => {
    if (cardsWrapperRef.current) {
      const cardWidth = 455 + cardSpacing;
      const scrollLeft = currentIndex * cardWidth;
      cardsWrapperRef.current.style.transform = `translateX(-${scrollLeft}px)`;
    }
  }, [currentIndex]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const newAccumulator = scrollAccumulator + event.deltaY;

      if (Math.abs(newAccumulator) >= scrollThreshold) {
        if (newAccumulator > 0 && !isLast) {
          onNext();
          setScrollAccumulator(0);
        } else if (newAccumulator < 0 && !isFirst) {
          onPrevious();
          setScrollAccumulator(0);
        } else {
          setScrollAccumulator(0);
        }
      } else {
        setScrollAccumulator(newAccumulator);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        container.removeEventListener("wheel", handleWheel);
      };
    }
  }, [scrollAccumulator, onNext, onPrevious, isFirst, isLast]);

  useEffect(() => {
    setScrollAccumulator(0);
  }, [currentIndex]);

  if (!items || items.length === 0) {
    return null;
  }

  const handleSentimentRated = () => {
    if (!isLast) {
      setTimeout(onNext, 300);
    }
  };

  return (
    <CarouselContainer ref={containerRef}>
      <CarouselContent>
        <ScrollContainer>
          <CardsWrapper ref={cardsWrapperRef}>
            {items.map((item, index) => (
              <CardContainer key={item.id || index}>
                <PerkCard
                  item={item}
                  onSentimentRated={handleSentimentRated}
                  active={index === currentIndex}
                  sentiments={sentiments}
                  onSentimentChange={onSentimentChange}
                />
              </CardContainer>
            ))}
          </CardsWrapper>
        </ScrollContainer>
      </CarouselContent>

      <BottomSection>
        <ProgressIndicators>
          {items.map((_, index) => (
            <ProgressDot
              key={index}
              active={index === currentIndex}
              onClick={() => onGoToItem(index)}
            />
          ))}
        </ProgressIndicators>

        <ActionButtons>
          {allItemsCompleted && (
            <Button color="primary" variant="contained" onClick={onNext}>
              {isLast ? "Complete" : "Next"}
            </Button>
          )}
        </ActionButtons>
      </BottomSection>
    </CarouselContainer>
  );
};
